import { useDetectionConfigurationMutation } from 'features/anomalyDetection'
import { useGeckoNotifications } from 'features/notifications'
import React, { createContext, useContext, useRef, useState } from 'react'
import { DetectionConfigurationData } from 'types'

import { EditDetectionConfigurationDataContext } from './EditDetectionConfigurationDataLayer'

interface EditDetectionConfigurationUXContextProps {
  inEditMode: boolean
  setInEditMode: React.Dispatch<React.SetStateAction<boolean>>
  handleSave: (formRef?: React.RefObject<HTMLFormElement | null>) => void
  handleEdit: () => void
  handleCancel: () => void
  handleSaveToRolodex: (entry: Partial<DetectionConfigurationData>) => void
  submitConfig: () => void
  formRef: React.RefObject<HTMLFormElement>
  isLoading: boolean
}

export const EditDetectionConfigurationUXContext =
  createContext<EditDetectionConfigurationUXContextProps>(null!)

export const EditDetectionConfigurationUXLayer: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const {
    detectionConfigurationId,
    detectionConfiguration,
    isNewConfiguration,
    mutateDetectionConfigurationLoadedEntry,
  } = useContext(EditDetectionConfigurationDataContext)
  const [inEditMode, setInEditMode] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const { successNotification, errorNotification } = useGeckoNotifications()
  const { post, patch, isLoading } = useDetectionConfigurationMutation()

  const handleEdit = () => {
    setInEditMode(true)
  }

  const handleCancel = () => {
    setInEditMode(false)
  }

  const handleSave = (formRef?: React.RefObject<HTMLFormElement | null>) => {
    if (formRef && formRef.current) {
      const event = new Event('submit', { bubbles: true, cancelable: true })
      formRef.current.dispatchEvent(event)
    }
  }

  const handleSaveToRolodex: EditDetectionConfigurationUXContextProps['handleSaveToRolodex'] =
    async (data) => {
      if (!data.detection_models || data.detection_models.length === 0) {
        errorNotification('At least one detection model is required')
        return
      }

      if (!data.target_sensor_entry_id || !data.polling_frequency_minutes) {
        errorNotification('Missing required fields')
        return
      }

      try {
        if (isNewConfiguration) {
          await post({
            name: data.name,
            target_sensor_entry_id: data.target_sensor_entry_id,
            polling_frequency_minutes: data.polling_frequency_minutes,
            detection_models: data.detection_models || [],
            is_enabled: data.is_enabled ?? true,
            overwrite: false,
          })
        } else {
          await patch({
            configurationId: detectionConfigurationId,
            data: {
              name: data.name,
              polling_frequency_minutes: data.polling_frequency_minutes,
              detection_models: data.detection_models,
              is_enabled: data.is_enabled,
            },
          })
        }

        setInEditMode(false)
        mutateDetectionConfigurationLoadedEntry()
        successNotification('Successfully saved detection configuration!')
      } catch (error) {
        errorNotification(
          'Failed to save detection configuration: ' + (error as Error).message
        )
      }
    }

  const submitConfig = () => {
    handleSave(formRef)
    handleSaveToRolodex(detectionConfiguration)
  }
  return (
    <EditDetectionConfigurationUXContext.Provider
      value={{
        inEditMode,
        setInEditMode,
        handleEdit,
        handleSave,
        handleCancel,
        handleSaveToRolodex,
        submitConfig,
        formRef,
        isLoading,
      }}>
      {children}
    </EditDetectionConfigurationUXContext.Provider>
  )
}
